import React from 'react'
import styled from 'styled-components'

import { Helmet } from 'react-helmet'

import { Box, Divider, Paper, Typography } from '@material-ui/core'

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: ${(props) => props.theme.spacing(8)}px ${(props) => props.theme.spacing(4)}px;
  background: ${(props) => props.theme.palette.background.default};
`

const Content = styled(Paper)`
  max-width: 860px;
  width: 100%;
  padding: ${(props) => props.theme.spacing(8)}px;

  ${(props) => props.theme.breakpoints.down('sm')} {
    padding: ${(props) => props.theme.spacing(4)}px;
  }
`

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box mt={4}>
    <Typography variant="h6" gutterBottom>{title}</Typography>
    <Typography variant="body2" color="textSecondary" component="div">
      {children}
    </Typography>
  </Box>
)

export const TermsOfUse = () => {
  return (
    <Wrapper>
      <Helmet title="Terms of Use" />
      <Content>
        <Typography variant="h4" gutterBottom>Terms of Use</Typography>
        <Typography variant="body2" color="textSecondary">
          Last updated: February 19, 2026
        </Typography>

        <Box mt={2}>
          <Typography variant="body1">
            These Terms of Use govern your access to and use of the SKUit warehouse management platform.
            By accessing or using SKUit, you agree to be bound by these terms. If you do not agree,
            do not use the platform.
          </Typography>
        </Box>

        <Divider style={{ marginTop: 24 }} />

        <Section title="1. Authorized Use">
          <p>
            Access to SKUit is granted solely to authorized users on behalf of a registered organization.
            You agree to use the platform only for lawful business purposes and in accordance with these
            terms and any applicable laws or regulations.
          </p>
          <p>You must not:</p>
          <ul>
            <li>Share your credentials with unauthorized individuals</li>
            <li>Attempt to access accounts, data, or systems you are not authorized to use</li>
            <li>Use the platform to store or transmit unlawful, harmful, or fraudulent content</li>
            <li>Reverse engineer, decompile, or attempt to extract source code from the platform</li>
            <li>Interfere with or disrupt the integrity or performance of the platform</li>
          </ul>
        </Section>

        <Section title="2. Account Responsibilities">
          <p>
            You are responsible for maintaining the confidentiality of your credentials and for all
            activity that occurs under your account. You must notify us immediately if you suspect
            unauthorized access or a security breach.
          </p>
          <p>
            Organization administrators are responsible for managing user access within their account,
            including provisioning and deprovisioning users in a timely manner.
          </p>
        </Section>

        <Section title="3. Data Ownership">
          <p>
            All data you and your organization directly provide to SKUit (inventory records, orders,
            shipments, facility data, and other content you submit) remains owned by your organization.
            SKUit does not claim ownership over data you provide.
          </p>
          <p>
            You grant SKUit a limited license to store, process, and display your data solely for the
            purpose of providing the platform services.
          </p>
          <p>
            All data generated by SKUit — including derived analytics, system-generated records,
            platform insights, aggregate metrics, and any other data produced by SKUit's systems
            independently of your direct input — is and remains the exclusive property of SKUit.
          </p>
        </Section>

        <Section title="4. Service Availability">
          <p>
            We aim to provide a reliable, highly available service, but we do not guarantee uninterrupted
            access. Scheduled maintenance, updates, or circumstances beyond our control may result in
            temporary unavailability. We will endeavor to provide advance notice of planned downtime.
          </p>
        </Section>

        <Section title="5. Intellectual Property">
          <p>
            SKUit and its underlying technology, design, and software are the intellectual property of
            SKUit and its licensors. Nothing in these terms grants you any rights to our intellectual
            property beyond the limited right to use the platform as described herein.
          </p>
        </Section>

        <Section title="6. Limitation of Liability">
          <p>
            To the fullest extent permitted by applicable law, SKUit shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages arising from your use of
            or inability to use the platform, even if we have been advised of the possibility of such
            damages.
          </p>
          <p>
            Our total liability for any claim arising under these terms shall not exceed the amount paid
            by your organization for the platform in the twelve months preceding the claim.
          </p>
        </Section>

        <Section title="7. Termination">
          <p>
            We reserve the right to suspend or terminate access to the platform at any time, with or
            without notice, for violations of these terms or for any other reason at our discretion.
            Upon termination, your right to use the platform ceases immediately.
          </p>
        </Section>

        <Section title="8. Changes to These Terms">
          <p>
            We may update these Terms of Use from time to time. Material changes will be communicated
            by posting the updated terms with a revised date. Continued use of the platform after
            changes are posted constitutes acceptance of the revised terms.
          </p>
        </Section>

        <Section title="9. Contact Us">
          <p>
            If you have questions about these Terms of Use, please contact us at:
          </p>
          <p>
            <strong>SKUit</strong><br />
            admin@skuit.com
          </p>
        </Section>
      </Content>
    </Wrapper>
  )
}
